import React, { useState, useEffect } from "react";
import {
  FaCode,
  FaBug,
  FaLightbulb,
  FaCommentAlt,
  FaFlask,
  FaExchangeAlt,
  FaMagic,
  FaSpinner,
} from "react-icons/fa";
import PaperDarkBG from "../kits/DesignKit/PaperDarkBG";
import MonoButton from "../kits/DesignKit/MonoButton";
import {
  checkOllamaStatus,
  explainCode,
  completeCode,
  fixCode,
  refactorCode,
  addCodeComments,
  generateTests,
  convertCode,
} from "../services/ollamaService";
import { useEditorSettings } from "../hooks/useEditorSettings";

function ZenEditorCodePanel({ selectedText, onInsertText, onReplaceText }) {
  const { settings } = useEditorSettings();
  const [ollamaStatus, setOllamaStatus] = useState({ available: false, models: [] });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [convertFrom, setConvertFrom] = useState("javascript");
  const [convertTo, setConvertTo] = useState("python");

  // Check Ollama status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkOllamaStatus();
      setOllamaStatus(status);
    };
    checkStatus();
  }, []);

  // Determine which model to use (prefer codellama, fallback to configured model)
  const getCodeModel = () => {
    const codelamaAvailable = ollamaStatus.models.some(m =>
      m.name.toLowerCase().includes('codellama')
    );
    return codelamaAvailable ? 'codellama' : settings.aiModel;
  };

  const handleAction = async (actionType, aiFunction) => {
    if (!selectedText?.trim()) {
      setError("Bitte markiere Code im Editor!");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setActiveAction(actionType);

    try {
      const model = getCodeModel();
      const response = await aiFunction(selectedText, model);

      if (response.success) {
        setResult(response.text);
      } else {
        setError(response.error || "AI-Anfrage fehlgeschlagen");
      }
    } catch (err) {
      setError(err.message || "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!selectedText?.trim()) {
      setError("Bitte markiere Code im Editor!");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setActiveAction("convert");

    try {
      const model = getCodeModel();
      const response = await convertCode(selectedText, convertFrom, convertTo, model);

      if (response.success) {
        setResult(response.text);
      } else {
        setError(response.error || "Konvertierung fehlgeschlagen");
      }
    } catch (err) {
      setError(err.message || "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  };

  const codelamaAvailable = ollamaStatus.models.some(m =>
    m.name.toLowerCase().includes('codellama')
  );

  const currentModel = getCodeModel();

  return (
    <PaperDarkBG className="rounded-lg">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FaCode className="text-brand text-lg" />
            <h3 className="text-sm font-semibold text-shadow">Code AI Assistant</h3>
          </div>
          <div className="text-xs text-gray-300">
            {ollamaStatus.available ? (
              <span className="text-green-400">
                ✓ {codelamaAvailable ? 'CodeLlama' : currentModel} verbunden
              </span>
            ) : (
              <span className="text-red-400">✗ Ollama nicht verfügbar</span>
            )}
          </div>
        </div>

        {/* Info Banner */}
        {!codelamaAvailable && ollamaStatus.available && (
          <div className="bg-orange-900/30 border border-orange-500/50 rounded px-3 py-2 mb-4">
            <p className="text-xs text-orange-200">
              💡 Für beste Ergebnisse installiere CodeLlama: <code className="text-xs bg-black/30 px-1 rounded">ollama pull codellama</code>
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <MonoButton
            onClick={() => handleAction("explain", explainCode)}
            disabled={loading || !ollamaStatus.available}
            className="text-xs px-3 py-2 flex items-center justify-center gap-2"
          >
            <FaLightbulb /> Erklären
          </MonoButton>

          <MonoButton
            onClick={() => handleAction("complete", completeCode)}
            disabled={loading || !ollamaStatus.available}
            className="text-xs px-3 py-2 flex items-center justify-center gap-2"
          >
            <FaMagic /> Vervollständigen
          </MonoButton>

          <MonoButton
            onClick={() => handleAction("fix", fixCode)}
            disabled={loading || !ollamaStatus.available}
            className="text-xs px-3 py-2 flex items-center justify-center gap-2"
          >
            <FaBug /> Bugs Fixen
          </MonoButton>

          <MonoButton
            onClick={() => handleAction("refactor", refactorCode)}
            disabled={loading || !ollamaStatus.available}
            className="text-xs px-3 py-2 flex items-center justify-center gap-2"
          >
            <FaCode /> Refactoren
          </MonoButton>

          <MonoButton
            onClick={() => handleAction("comment", addCodeComments)}
            disabled={loading || !ollamaStatus.available}
            className="text-xs px-3 py-2 flex items-center justify-center gap-2"
          >
            <FaCommentAlt /> Kommentieren
          </MonoButton>

          <MonoButton
            onClick={() => handleAction("test", generateTests)}
            disabled={loading || !ollamaStatus.available}
            className="text-xs px-3 py-2 flex items-center justify-center gap-2"
          >
            <FaFlask /> Tests Generieren
          </MonoButton>
        </div>

        {/* Code Conversion Section */}
        <div className="border-t border-gray-600 pt-4 mb-4">
          <h4 className="text-xs font-semibold mb-2 flex items-center gap-2">
            <FaExchangeAlt className="text-brand" />
            Code Konvertieren
          </h4>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-xs text-gray-300 block mb-1">Von:</label>
              <select
                value={convertFrom}
                onChange={(e) => setConvertFrom(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded"
              >
                <option value="javascript">JavaScript</option>
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="go">Go</option>
                <option value="rust">Rust</option>
                <option value="typescript">TypeScript</option>
                <option value="php">PHP</option>
                <option value="ruby">Ruby</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-300 block mb-1">Nach:</label>
              <select
                value={convertTo}
                onChange={(e) => setConvertTo(e.target.value)}
                className="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-600 rounded"
              >
                <option value="python">Python</option>
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
                <option value="java">Java</option>
                <option value="go">Go</option>
                <option value="rust">Rust</option>
                <option value="php">PHP</option>
                <option value="ruby">Ruby</option>
              </select>
            </div>
          </div>
          <MonoButton
            onClick={handleConvert}
            disabled={loading || !ollamaStatus.available || convertFrom === convertTo}
            className="text-xs px-3 py-2 w-full flex items-center justify-center gap-2"
          >
            <FaExchangeAlt /> Konvertieren
          </MonoButton>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-blue-900/30 border border-blue-500/50 rounded px-3 py-3 mb-4">
            <div className="flex items-center gap-2 text-blue-200">
              <FaSpinner className="animate-spin" />
              <span className="text-xs">AI verarbeitet deinen Code...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-900/30 border border-red-500/50 rounded px-3 py-3 mb-4">
            <p className="text-xs text-red-200">❌ {error}</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div className="bg-gray-800 border border-gray-600 rounded p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-300">
                {activeAction === "explain" && "📝 Erklärung"}
                {activeAction === "complete" && "✨ Vervollständigung"}
                {activeAction === "fix" && "🔧 Behobener Code"}
                {activeAction === "refactor" && "♻️ Refactored Code"}
                {activeAction === "comment" && "💬 Kommentierter Code"}
                {activeAction === "test" && "🧪 Unit Tests"}
                {activeAction === "convert" && `🔄 ${convertFrom} → ${convertTo}`}
              </h4>
              <div className="flex gap-1">
                {activeAction !== "explain" && (
                  <MonoButton
                    onClick={() => onReplaceText(result)}
                    className="text-xs px-2 py-1"
                  >
                    Ersetzen
                  </MonoButton>
                )}
                <MonoButton
                  onClick={() => onInsertText(result)}
                  className="text-xs px-2 py-1"
                >
                  {activeAction === "explain" ? "Als Kommentar einfügen" : "Einfügen"}
                </MonoButton>
              </div>
            </div>
            <pre className="text-xs  bg-[#151515] p-3 rounded overflow-x-auto text-green-400 whitespace-pre-wrap">
              {result}
            </pre>
          </div>
        )}

        {/* Help Text */}
        {!selectedText && !loading && !result && (
          <div className="text-xs text-gray-400 text-center py-4">
            Markiere Code im Editor und wähle eine Aktion
          </div>
        )}
      </div>
    </PaperDarkBG>
  );
}

export default ZenEditorCodePanel;
